import { CountBadge } from 'components/CountBadge';
import { MenuItem } from 'components/navigation/KebabMenu';
import {
    RECORD_GRID_CLASS,
    RECORD_PANEL_CLASS,
    RECORD_PANEL_ROW_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider, useEditingSectionId } from 'components/section-edit/SectionEdit';
import SectionUnsavedChangesGuard from 'components/section-edit/SectionUnsavedChangesGuard';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { InvoiceCaseSummaries, useInvoiceTabAlerts } from 'pages/invoices/hooks/useInvoiceTabAlerts';
import InvoiceWorkflow from 'pages/invoices/InvoiceWorkflow';
import { countTabActions, INVOICE_TAB, InvoiceTabId } from 'pages/invoices/util/invoiceTabAlerts';
import { ComponentProps, ComponentType, ReactNode, Ref, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InvoiceType } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { CasesTab } from './CasesTab';
import { ExpensesTab } from './ExpensesTab';
import { GeneralTab } from './GeneralTab';
import { InvoiceFlowNotice } from './InvoiceFlowNotice';
import { InvoiceRecordHeader } from './InvoiceRecordHeader';
import { InvoiceSideColumn, SIDE_COLLAPSED_GRID_CLASS } from './InvoiceSideColumn';
import { InvoiceStepper } from './InvoiceStepper';
import { BILLING_NOTES_SECTION_ID, tabOfSection } from './invoiceSections';
import { InvoiceTabProps } from './invoiceTabProps';
import { TimecardsTab } from './TimecardsTab';
import { InvoiceMoreMenuArgs, useInvoiceMoreMenu } from './useInvoiceMoreMenu';

// Old links to the Totals tab open Cases, which holds each case's totals.
const TAB_ALIASES = { totals: [INVOICE_TAB.CASES] };

const TABS: { id: InvoiceTabId; labelId: string; Body: ComponentType<InvoiceTabProps> }[] = [
    { id: INVOICE_TAB.GENERAL, labelId: 'invoices.general', Body: GeneralTab },
    { id: INVOICE_TAB.CASES, labelId: 'invoices.details.title', Body: CasesTab },
    { id: INVOICE_TAB.TIMECARDS, labelId: 'timecards.timecards', Body: TimecardsTab },
    { id: INVOICE_TAB.EXPENSES, labelId: 'expenses.expenses', Body: ExpensesTab },
];

type HeaderProps = ComponentProps<typeof InvoiceRecordHeader>;

interface Props extends Omit<InvoiceTabProps, 'alerts'> {
    templateValidation: () => Promise<boolean>;
    onPostponeSuccess: () => void;
    isInvoiceEmpty: boolean;
    isInvoiceContentLoaded: boolean;
    onOpenHistory: HeaderProps['onOpenHistory'];
    comments: HeaderProps['comments'];
    menu: Omit<InvoiceMoreMenuArgs, 'invoice' | 'isInViewMode'>;
    /** A notice of the invoice's own under the header, such as a shared payment's incomplete shares. */
    notice?: ReactNode;
    /** Told whether a card is open in edit, for what the page saves outside the cards to wait for it. */
    onEditingChange: (isEditing: boolean) => void;
    /** The comments docked beside the panel, under the band and its tab strip. */
    commentsColumn?: ReactNode;
    /** Set on the row the panel shares with `commentsColumn`, for the page to measure the room for it. */
    rowRef?: Ref<HTMLDivElement>;
}

/**
 * The invoice page in the new design, on the record page: the band with the header, the flow's notice and the
 * stepper; the tab strip; then the tab's cards with the side column beside them, and the docked comments beside
 * those. Only the left column is the tab panel, so the side column and the comments stay mounted across tabs. One card edits at a time, and while one does the workflow
 * waits and leaving the page asks first.
 */
export function InvoiceRecordPage(props: Props) {
    return (
        // Each card gates its own Edit: some, such as Debt collection, still edit on an invoice that no longer does.
        <SectionEditProvider canEdit>
            <InvoiceRecordBody {...props} />
        </SectionEditProvider>
    );
}

function InvoiceRecordBody({
    templateValidation,
    onPostponeSuccess,
    isInvoiceEmpty,
    isInvoiceContentLoaded,
    onOpenHistory,
    comments,
    menu,
    notice,
    onEditingChange,
    commentsColumn,
    rowRef,
    ...tabProps
}: Props) {
    const { invoice, classifiers, isInViewMode, isLoading, successMessages, handleStatusLoadingState } = tabProps;
    const intl = useIntl();
    const editingSectionId = useEditingSectionId();
    const isEditing = editingSectionId !== null;
    useEffect(() => {
        onEditingChange(isEditing);
        return () => onEditingChange(false);
    }, [isEditing, onEditingChange]);
    const alerts = useInvoiceTabAlerts({
        invoice,
        isInViewMode,
        successMessages,
        editingTab: editingSectionId ? tabOfSection(editingSectionId) : null,
    });
    const counts = countTabActions(alerts);
    // Kept while the page is, as classic's drawer keeps it. Its Billing notes card shows while it edits, opened from
    // the payer's card too.
    const [isSideOpen, setIsSideOpen] = useState(true);
    const isSideShown = isSideOpen || editingSectionId === BILLING_NOTES_SECTION_ID;
    const menuItems: MenuItem[] = useInvoiceMoreMenu({ invoice, isInViewMode, ...menu });

    // A prepayment invoice bills no timecards or expenses.
    const shownTabs =
        invoice.invoiceTypeCode === InvoiceType.PREPAYMENT
            ? TABS.filter(({ id }) => id === INVOICE_TAB.GENERAL || id === INVOICE_TAB.CASES)
            : TABS;
    const tabs: TabType[] = shownTabs.map(({ id, labelId, Body }) => {
        const count = counts[id] ?? 0;
        return {
            id,
            label: <FormattedMessage id={labelId} />,
            icon: count ? (
                <CountBadge count={count} noun={intl.formatMessage({ id: 'invoices.tab-count-noun' }, { count })} />
            ) : undefined,
            component: <Body {...tabProps} alerts={alerts.filter((alert) => alert.tab === id)} />,
        };
    });

    return (
        <>
            <SectionUnsavedChangesGuard when={editingSectionId !== null} />
            <InvoiceCaseSummaries invoice={invoice} isInViewMode={isInViewMode} />
            <RecordPage
                header={
                    <InvoiceWorkflow
                        invoice={invoice}
                        handleStatusLoadingState={handleStatusLoadingState}
                        isLoading={isLoading}
                        templateValidation={templateValidation}
                        onPostponeSuccess={onPostponeSuccess}
                        isInvoiceEmpty={isInvoiceEmpty}
                        isInvoiceContentLoaded={isInvoiceContentLoaded}
                        hasOpenEdit={editingSectionId !== null}
                        renderBand={({ stages, pending, actions, extras }) => (
                            <>
                                <InvoiceRecordHeader
                                    invoice={invoice}
                                    classifiers={classifiers}
                                    workflowActions={actions}
                                    menuItems={menuItems}
                                    onOpenHistory={onOpenHistory}
                                    comments={comments}
                                />
                                <InvoiceFlowNotice invoice={invoice} />
                                {notice}
                                {extras}
                                <InvoiceStepper stages={stages} pending={pending} />
                            </>
                        )}
                    />
                }
            >
                <TabsGallery
                    tabs={tabs}
                    tabAliases={TAB_ALIASES}
                    arrows={{ back: t('back'), next: t('next') }}
                    tabGroupId="invoices-center-block"
                    trackTabsInHistoryState
                    usePathBasedRouting
                    wrapperClassName={RECORD_TABS_CLASS}
                    className={RECORD_TAB_STRIP_CLASS}
                    panelClassName="flex min-w-0 flex-col gap-(--card-gap)"
                    panelFrame={(panel) => (
                        <div ref={rowRef} className={RECORD_PANEL_ROW_CLASS}>
                            <div className="flex min-w-0 flex-1 flex-col">
                                <div className={cn(RECORD_PANEL_CLASS, commentsColumn && 'pe-0')}>
                                    <div className={cn(RECORD_GRID_CLASS, !isSideShown && SIDE_COLLAPSED_GRID_CLASS)}>
                                        {panel}
                                        <InvoiceSideColumn
                                            invoice={invoice}
                                            isInViewMode={isInViewMode}
                                            isLoading={isLoading}
                                            templateValidation={templateValidation}
                                            isOpen={isSideShown}
                                            onToggle={() => setIsSideOpen(!isSideShown)}
                                        />
                                    </div>
                                </div>
                            </div>
                            {commentsColumn ? (
                                <div className="flex shrink-0 pe-(--page-gutter-x) pt-6 pb-(--page-gutter-y)">
                                    {commentsColumn}
                                </div>
                            ) : null}
                        </div>
                    )}
                />
            </RecordPage>
        </>
    );
}
